import { useState } from 'react';
import { Save, X } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import type { Day, DayFormData } from '../../types';

interface DayFormProps {
  weekId: string;
  initialData?: Day;
  onSubmit: (data: DayFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const dayOptions = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' },
];

export function DayForm({ weekId, initialData, onSubmit, onCancel, isLoading }: DayFormProps) {
  const [formData, setFormData] = useState({
    dayNumber: initialData?.dayNumber || 1,
    shift: (initialData?.shift ?? '') as '' | NonNullable<DayFormData['shift']>,
    name: initialData?.name || '',
    notes: initialData?.notes || '',
    restDay: initialData?.restDay || false,
    weekId: weekId,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // shift vacío => null para poder limpiarlo en el backend.
    onSubmit({ ...formData, shift: formData.shift || null });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox'
        ? (e.target as HTMLInputElement).checked
        : name === 'dayNumber'
          ? parseInt(value, 10)
          : value,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="dayNumber" className="label">Día de la Semana *</label>
          <select
            id="dayNumber"
            name="dayNumber"
            value={formData.dayNumber}
            onChange={handleChange}
            className="input"
          >
            {dayOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="shift" className="label">Turno</label>
          <select
            id="shift"
            name="shift"
            value={formData.shift}
            onChange={handleChange}
            className="input"
          >
            <option value="">Sin turno</option>
            <option value="morning">Mañana</option>
            <option value="afternoon">Tarde</option>
            <option value="evening">Noche</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="name" className="label">Nombre (opcional)</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="input"
          placeholder="Ej: Tren Superior"
        />
      </div>

      <div>
        <label htmlFor="notes" className="label">Notas</label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={2}
          className="input resize-none"
          placeholder="Indicaciones para el día..."
        />
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          name="restDay"
          checked={formData.restDay}
          onChange={handleChange}
          className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500"
        />
        <span className="text-dark-300 text-sm">Día de descanso</span>
      </label>

      <div className="flex justify-end gap-3 pt-4 border-t border-dark-700">
        <button type="button" onClick={onCancel} className="btn-secondary">
          <X className="w-4 h-4" />
          Cancelar
        </button>
        <button type="submit" disabled={isLoading} className="btn-primary">
          {isLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              {initialData ? 'Guardar' : 'Crear Día'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

